import { c as _c } from "react/compiler-runtime";
import { useRef, useState } from 'react';
import { throwIfAborted } from '../abort.js';
import { forgetFundingCustomer, fundingCustomerId } from '../discovery/discover-checkout.js';
import { previouslyUsedCards } from '../discovery/saved-cards.js';
import { FundingProviderError } from '../provider-error.js';
export function useCardCheckout(context) {
  const $ = _c(15);
  const [card, setCard] = useState(null);
  const [previouslyUsed, setPreviouslyUsed] = useState(false);
  const [entering, setEntering] = useState(false);
  const entryActive = useRef(false);
  let t0;
  if ($[0] !== card || $[1] !== context) {
    t0 = async function continueCard(operation, signal, onReady) {
      if (operation.method !== "card") {
        throw new Error("invalid_card_operation");
      }
      const customerId = fundingCustomerId(context.provider);
      if (!customerId) {
        context.navigation.transition("connect_requested");
        return;
      }
      const customer = await context.provider.getCustomer(customerId);
      throwIfAborted(signal);
      if (!customer.ok) {
        const failure = new FundingProviderError(customer.error);
        context.onFailure("card_validation");
        if (failure.failure.category === "connection_required") {
          forgetFundingCustomer(context.provider);
          context.navigation.transition("connect_requested");
          return;
        }
        throw failure;
      }
      if (customer.value.data.id !== customerId) {
        throw new Error("onramp_customer_mismatch");
      }
      const methods = await context.provider.getPaymentMethods({
        currency: operation.fiatCurrency
      });
      throwIfAborted(signal);
      if (!methods.ok) {
        throw new FundingProviderError(methods.error);
      }
      const config = methods.value.data.paymentMethodConfigs?.find(_temp);
      if (!config?.availability.active || config.capabilities.requiresWidget || !config.capabilities.supportedCurrencies.includes(operation.fiatCurrency) || !config.capabilities.supportedTransactionTypes.includes("buy")) {
        setCard(null);
        context.onFailure("card_validation");
        context.navigation.transition("card_unavailable");
        throw new Error("card_unavailable");
      }
      if (!card) {
        entryActive.current = true;
        setEntering(true);
        context.navigation.transition("card_entry_required");
        return;
      }
      const current = previouslyUsedCards(methods.value.data.paymentMethods ?? [], [card.id], config.capabilities.allowsDeletion === true)[0];
      if (!current?.availability.active) {
        setCard(null);
        context.onFailure("card_validation");
        context.navigation.transition("card_unavailable");
        throw new Error("card_unavailable");
      }
      setCard(current);
      await context.quote(operation, current.id, signal, onReady);
    };
    $[0] = card;
    $[1] = context;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const continueCard = t0;
  let t1;
  if ($[3] !== context) {
    t1 = function cardEntryEvent(event) {
      if (!entryActive.current || !context.operation || context.navigation.getSnapshot().checkout !== "card_entry") {
        return;
      }
      if (event.kind === "error") {
        entryActive.current = false;
        setEntering(false);
        context.run(_temp2);
      } else {
        if (event.kind === "complete") {
          entryActive.current = false;
          const added = {
            ...event.payload.card,
            allowsDeletion: false
          };
          setCard(added);
          const operation_0 = context.operation;
          context.run(signal_0 => context.quote(operation_0, added.id, signal_0)).then(() => {
            setEntering(false);
          });
        }
      }
    };
    $[3] = context;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const cardEntryEvent = t1;
  let t2;
  if ($[5] !== card?.id || $[6] !== context) {
    t2 = async function deleteCard(id, cards) {
      const eligible = cards.find(candidate => candidate.id === id && candidate.allowsDeletion);
      if (!eligible || !context.provider.deletePaymentMethod) {
        return;
      }
      await context.run(async signal_1 => {
        const result = await context.provider.deletePaymentMethod?.(id);
        throwIfAborted(signal_1);
        if (result && !result.ok) {
          context.onFailure("card_deletion");
          throw new FundingProviderError(result.error);
        }
        if (card?.id === id) {
          setCard(null);
        }
      });
    };
    $[5] = card?.id;
    $[6] = context;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const deleteCard = t2;
  let t3;
  if ($[8] !== card || $[9] !== cardEntryEvent || $[10] !== continueCard || $[11] !== deleteCard || $[12] !== entering || $[13] !== previouslyUsed) {
    t3 = {
      card,
      previouslyUsed,
      continueCard,
      cardEntryEvent,
      deleteCard,
      selectCard(next) {
        entryActive.current = false;
        setCard(next);
        setPreviouslyUsed(next !== null);
        setEntering(false);
      },
      cardEntryActive: entering,
      retryCardEntry() {
        entryActive.current = true;
        setEntering(true);
      }
    };
    $[8] = card;
    $[9] = cardEntryEvent;
    $[10] = continueCard;
    $[11] = deleteCard;
    $[12] = entering;
    $[13] = previouslyUsed;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  return t3;
}
async function _temp2() {
  throw new Error("card_entry_failed");
}
function _temp(method) {
  return method.type === "card";
}